<script setup lang="ts">
import CsButton from '@/components/ui/button/CsButton.vue'
import AccountInfoForm from '@/modules/account/components/AccountInfoForm.vue'
import AccountKYCForm from '@/modules/account/components/AccountKYCForm.vue'
import MembershipPlanForm from '@/modules/account/components/MembershipPlanForm.vue'
import ReferalList from '@/modules/account/components/ReferalList.vue'
import { AccountDetailActionType, useAccountDetailStore } from '@/modules/account/stores/account.detail.store'
import TransactionByAccount from '@/modules/transaction/components/TransactionByAccount.vue'
import DepositPanel from '@/modules/transaction/views/DepositPanel.vue'
import WithdrawPanel from '@/modules/transaction/views/WithdrawPanel.vue'
import Modal from '@/onenetwork/components/modal/Modal.vue'
import AllowPerm from '@/onenetwork/components/perms/AllowPerm.vue'
import { numHelper } from '@/utils/numHelper.ts'
import { Icon } from '@iconify/vue'
import { useConfirm } from 'primevue'
import { useToast } from 'primevue/usetoast'
import { computed, onMounted, ref } from 'vue'

interface Props {
  id: string | number
  defautAction?: string
  isUpdate?: boolean
}
const confirm = useConfirm()
const loadingSubmit = ref(false)
const type = ref('')
const props = defineProps<Props>()

const AccountDetailStore = useAccountDetailStore()
const toast = useToast()
const displayDepositPanel = ref(props.defautAction === 'deposit')
const displayWithdrawPanel = ref(props.defautAction === 'withdraw')

const fetched = computed(() => {
  return AccountDetailStore.accounts[props.id]?.action_type
})

const requestFetchData = () => {
  try{
    AccountDetailStore.fetchAccountDetail(Number(props?.id))
  } catch (e: unknown) {
  }
}

const account: any = computed(() => {
  return AccountDetailStore.accounts[props.id]?.account || null
})

const currentTab = ref('1')

const handleTabChange = (val: string | number) => {
  currentTab.value = val as string
}

onMounted(() => {
  requestFetchData()
})

const handleKycAction = async (action: 'approve' | 'reject', id: number) => {
  loadingSubmit.value = true;
  const res = action === 'approve'
    ? await AccountDetailStore.approveKyc(id)
    : await AccountDetailStore.rejectKyc(id);

  const isSuccess = res.status === 200;
  const actionText = action === 'approve' ? 'Phê duyệt' : 'Từ chối';

  toast.add({
    severity: isSuccess ? 'success' : 'error',
    summary: isSuccess ? 'Success' : 'Rejected',
    detail: `${actionText} ${isSuccess ? 'thành công' : 'thất bại'}`,
    life: 3000
  });

  if (isSuccess) {
    setTimeout(() => {
      loadingSubmit.value = false;
      requestFetchData()
    }, 1000);
  }else{
    loadingSubmit.value = false;
  }

};

const handleSubmit = async (action: 'approve' | 'reject', id: number) => {
  type.value = action
  confirm.require({
    message: `Bạn có chắc chắn muốn ${action === 'approve' ? 'phê duyệt' : 'từ chối'} KYC tài khoản này không?`,
    header: 'Xác nhận',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Đồng ý'
    },
    accept: () => handleKycAction(action, id),
    reject: () => {}
  });
}

</script>

<template>
  <template v-if="AccountDetailActionType.FETCHING === fetched">
    <Card>
      <template #content>
        <div class="flex gap-3">
          Đang tải dữ liệu <Icon icon="eos-icons:three-dots-loading" width="24" height="24" />
        </div>
      </template>

    </Card>
  </template>
  <template v-if="AccountDetailActionType.FETCH_FAILED === fetched">
    <Card class="bg-red-300">
      <template #content>
        <div class="flex flex-col gap-3 items-center">
          <Icon icon="fluent-color:error-circle-16" width="50" />
          <span class="font-300 text-[16px]">Lỗi khi tải dữ liệu này</span>
        </div>
      </template>

    </Card>
  </template>
  <template v-if="AccountDetailActionType.FETCHED === fetched">
    <Panel v-if="account" >
      <template #header>
        <div class="flex items-center justify-between w-full">
          <h3 class="text-base font-semibold p-panel-title">Thông tin tài khoản: [#{{ account?.code }}] {{ account?.full_name }}</h3>
          <div class="flex gap-2 pl-4 ml-4" v-if="account?.kyc_information?.processing_status === 'pending'">
            <AllowPerm perm="account.approve_kyc" action="hide">
              <!-- <button
                :class="['btn flex items-center gap-2', loadingSubmit ? 'btn-disabled' : 'btn-success']"
                :disabled="loadingSubmit"
                @click="handleSubmit('approve', account?.kyc_information?.id)">
                <Icon icon="mdi:check-all" width="20" height="20" />
                <div :class="[loadingSubmit ? 'flex items-center gap-2' : '']">
                  Phê duyệt KYC <Icon icon="eos-icons:three-dots-loading" width="24" height="24" v-if="loadingSubmit && type === 'approve'" />
                </div>
              </button> -->
              <CsButton
                type="button"
                :loading="loadingSubmit && type === 'approve'"
                :disabled="loadingSubmit"
                variant="success"
                icon="mdi:check-all"
                text="Phê duyệt KYC"
                @click="handleSubmit('approve', account?.kyc_information?.id)"
              />
              <!-- <button
                :class="['btn flex items-center gap-2', loadingSubmit ? 'btn-disabled' : 'btn-danger']"
                :disabled="loadingSubmit"
                @click="handleSubmit('reject', account?.kyc_information?.id)">
                <Icon icon="healthicons:i-documents-denied" width="20" height="20" />
                <div :class="[loadingSubmit ? 'flex items-center gap-2' : '']">
                  Từ chối phê duyệt <Icon icon="eos-icons:three-dots-loading" width="24" height="24" v-if="loadingSubmit && type === 'reject'" />
                </div>
              </button> -->
              <CsButton
                type="button"
                :loading="loadingSubmit && type === 'reject'"
                :disabled="loadingSubmit"
                variant="danger"
                icon="healthicons:i-documents-denied"
                text="Từ chối phê duyệt"
                @click="handleSubmit('reject', account?.kyc_information?.id)"
              />
            </AllowPerm>
          </div>
        </div>
      </template>
      <div class="flex flex-col md:flex-row gap-5">
        <AccountInfoForm
          :account="account"
          :is-update="isUpdate"
        />
        <div class="flex-1 md:flex-3">
          <div class="flex flex-col gap-3 p-[24px]">
            <div class="flex gap-10">
              <div class="">
                <Icon icon="et:wallet" width="48"/>
              </div>
              <div class="flex flex-col justify-start">
                <div class="flex mb-1 gap-3">
                  <div class="">Tiền mặt đang có:</div>
                  <template v-if="account?.wallet?.cash_balance > 0">
                      <div class="flex-1 font-bold text-[#199900]">
                        {{ numHelper(account.wallet.cash_balance).format() }}<sup>đ</sup>
                      </div>
                    </template>
                  <template v-else>-</template>
                </div>
                <div class="flex gap-3">
                  <div class="">Điểm đang có:</div>
                  <div class="flex-1 font-bold text-[#f86c00]">{{ account?.wallet?.point_balance > 0 ? numHelper(account?.wallet?.point_balance).format() : '-'}}</div>
                </div>
              </div>
            </div>
            <div class="flex flex-row gap-2">
              <AllowPerm perm="account.deposit" action="hide">
                <CsButton
                  type="button"
                  icon="tabler:moneybag-plus"
                  text="Nạp tiền"
                  @click="()=>{displayDepositPanel = true}"
                />
              </AllowPerm>
              <AllowPerm perm="account.withdraw" action="hide">
                <CsButton
                  type="button"
                  variant="danger"
                  icon="tabler:moneybag-minus"
                  text="Rút tiền"
                  @click="()=>{displayWithdrawPanel = true}"
                />
              </AllowPerm>
            </div>
          </div>
          <Divider/>

          <MembershipPlanForm
            :account="account"
            :is-update="isUpdate"
          />
        </div>
      </div>
    </Panel>
    <Tabs class="mt-3" :value="currentTab" @update:value="handleTabChange">
      <TabList>
        <Tab value="1">Thông tin KYC</Tab>
        <Tab value="8">Lịch sử giao dịch</Tab>
        <Tab value="2">Giới thiệu thành viên</Tab>
      </TabList>
      <TabPanels>
        <TabPanel value="1">
          <AccountKYCForm :is-update="isUpdate" :account="account" :current-tab="currentTab"/>
        </TabPanel>
        <TabPanel value="8">
          <div class="flex flex-col md:flex-row gap-x-10 items-start">
            <div class="flex-1">
              <TransactionByAccount :account="account" :current-tab="currentTab"/>
            </div>
          </div>
        </TabPanel>
        <TabPanel value="2">
          <div class="flex flex-col md:flex-row gap-x-10 items-start">
            <div class="flex-1">
              <ReferalList :account="account" :current-tab="currentTab"/>
            </div>

          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>


  </template>
  <Modal :show="displayDepositPanel" title="Nạp tiền vào tài khoản" @on:close="()=>{displayDepositPanel = false}">
    <DepositPanel :account="account" @update:closeModal="() => {
      displayDepositPanel = false
      // requestFetchData()
      AccountDetailStore.fetchAccountDetail(Number(props?.id), 'update')
    }"/>
  </Modal>
  <Modal :show="displayWithdrawPanel" title="Rút tiền từ tài khoản" @on:close="()=>{displayWithdrawPanel = false}">
    <WithdrawPanel :account="account" @update:closeModal="()=>{
      displayWithdrawPanel = false
      // requestFetchData()
      AccountDetailStore.fetchAccountDetail(Number(props?.id), 'update')
    }"/>
  </Modal>
</template>

<style scoped>

</style>
